import { IConfigurableProduct } from 'models/IConfigurableProduct';
import { VariantConfigurator } from 'models/VariantConfigurator';
import {
  InstallationConfigurator,
  PolarpumpInstallationConfigurator,
  LawnMowerInstallationConfigurator,
  VvsInstallationConfigurator,
  ZipCodeExcludeConfigurator
} from 'models/InstallationConfigurator';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { ConfiguratorType } from 'models/ConfiguratorType';
import { SlidingDoorConfigurator } from 'models/SlidingDoorConfigurator';
import { IConstraint } from 'models/IConstraint';
import { ConstraintType } from 'models/ConstraintType';
import { Min, Max, MinMax } from 'models/Constraints';
import { BasePageModel } from 'models/BasePageModel';
import { isProductPage } from 'models/ProductPageModel';
import { ElfaConfigurator } from 'models/ElfaConfigurator';
import { isValueConstraint } from './rules';
import { ProductAttribute } from 'models/ProductAttribute';
import { PageState } from 'models/PageState';

export const isConfigurable = (
  currentPage: unknown
): currentPage is Required<IConfigurableProduct> => {
  const config =
    (currentPage as IConfigurableProduct).configurator !== undefined &&
    (currentPage as IConfigurableProduct).configurator;

  return Boolean(config && config.type !== null);
};

interface GetAttributeProps {
  attributes: ProductAttribute[][];
  id: string;
}

export const getAttribute = ({ attributes, id }: GetAttributeProps) => {
  return attributes.reduce((_, curr) => {
    const attribute = curr.find(a => a.id.toLowerCase() === id);
    return attribute ? attribute : undefined;
  }, undefined);
};

export const getConfigurator = (currentPage: BasePageModel): ConfiguratorBase =>
  isConfigurable(currentPage) ? currentPage.configurator : ({} as ConfiguratorBase);

export type Nullable<T> = { [P in keyof T]?: T[P] | null };

function configType<T extends ConfiguratorBase>(
  type: ConfiguratorType | ((config: Nullable<ConfiguratorBase>) => boolean)
) {
  return (configurator: Nullable<ConfiguratorBase>): configurator is T => {
    if (typeof type === 'function') {
      return type(configurator);
    }
    return configurator.type === type;
  };
}
export const isVariantConfigurator = configType<VariantConfigurator>(ConfiguratorType.Default);
export const isSlidingDoorConfigurator = configType<SlidingDoorConfigurator>(
  ConfiguratorType.SlidingDoor
);
export const isKitchenContactConfigurator = configType(ConfiguratorType.KitchenContact);
export const isNotForSaleConfigurator = configType(ConfiguratorType.NotForSale);
export const isShowerDoorConfigurator = configType(ConfiguratorType.ShowerDoor);

export const isZipCodeExcludeConfigurator = configType<ZipCodeExcludeConfigurator>(
  ConfiguratorType.ZipCodeExclude
);

export const isElfaConfigurator = configType<ElfaConfigurator>(ConfiguratorType.Elfa);

const INSTALLATION_TYPES = [
  ConfiguratorType.LawnmowerInstallation,
  ConfiguratorType.PolarPumpInstallation,
  ConfiguratorType.VvsInstallation
];
export const isInstallationConfigurator = configType<InstallationConfigurator>(
  configurator =>
    !!configurator?.type &&
    configurator.type !== undefined &&
    configurator.type !== null &&
    INSTALLATION_TYPES.includes(configurator.type) &&
    (configurator as InstallationConfigurator).installation !== undefined
);
export const isPolarPumpConfigurator = configType<PolarpumpInstallationConfigurator>(
  ConfiguratorType.PolarPumpInstallation
);
export const isLawnMowerConfigurator = configType<LawnMowerInstallationConfigurator>(
  ConfiguratorType.LawnmowerInstallation
);

export const isVvsConfigurator = configType<VvsInstallationConfigurator>(
  ConfiguratorType.VvsInstallation
);

interface ConfiguratorProps {
  configurator: ConfiguratorBase;
}

export const getConfiguratorAddons = ({ configurator }: ConfiguratorProps) => {
  if (
    configurator &&
    !isLawnMowerConfigurator(configurator) &&
    !isPolarPumpConfigurator(configurator) &&
    !isVvsConfigurator(configurator)
  ) {
    return [];
  }

  return configurator?.addons?.products || [];
};

export const getConfiguratorInstallations = ({ configurator }: ConfiguratorProps) => {
  if (
    configurator &&
    !isLawnMowerConfigurator(configurator) &&
    !isPolarPumpConfigurator(configurator) &&
    !isVvsConfigurator(configurator)
  ) {
    return [];
  }

  return configurator?.installation.products || [];
};

export function isConstraintType<T extends IConstraint>(type: ConstraintType) {
  return (constraint?: IConstraint): constraint is T =>
    constraint !== undefined && constraint.rule !== undefined && constraint.rule === type;
}

export const getAddons = (currentPage: BasePageModel) =>
  isProductPage(currentPage) ? currentPage.addons : {};

export const isMinConstraint = isConstraintType<Min>(ConstraintType.Min);
export const isMaxConstraint = isConstraintType<Max>(ConstraintType.Max);
export const isMinMaxConstraint = isConstraintType<MinMax>(ConstraintType.MinMax);

const getMinDoorsConstraint = ({ constraints }: ConfiguratorBase) => {
  return constraints.find(c => c.attribute === 'doors' && c.rule === ConstraintType.Min);
};
const slidingDoorModifier = (config: ConfiguratorBase) => {
  const minDoorsConstraint = getMinDoorsConstraint(config);

  if (
    config.values &&
    'widthMm' in config.values &&
    'heightMm' in config.values &&
    'doors' in config.values
  ) {
    if (
      isValueConstraint(minDoorsConstraint) &&
      typeof minDoorsConstraint.value === 'number' &&
      Number(config.values.doors) < minDoorsConstraint.value
    ) {
      return minDoorsConstraint.value;
    }

    return Number(config.values.doors);
  }

  if (isValueConstraint(minDoorsConstraint) && typeof minDoorsConstraint.value === 'number') {
    return minDoorsConstraint.value;
  }

  return 1;
};

const elfaModifier = (config: ConfiguratorBase) => {
  if (config.values && 'doors' in config.values) {
    return Number(config.values.doors);
  }

  const minDoorsConstraint = getMinDoorsConstraint(config);
  if (isValueConstraint(minDoorsConstraint) && typeof minDoorsConstraint.value === 'number') {
    return minDoorsConstraint.value;
  }

  return 1;
};
export const configuratorModifier = (config?: ConfiguratorBase) => {
  if (config === undefined) {
    return 1;
  }
  switch (config.type) {
    case ConfiguratorType.SlidingDoor:
      return slidingDoorModifier(config);
    case ConfiguratorType.Elfa:
      return elfaModifier(config);
    default:
      return 1;
  }
};

export const configuratorValues = (state: PageState) =>
  isProductPage(state.currentPage) ? state.currentPage.configurator?.values : undefined;
